import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { parseQuery } from '@gonfalon/dataviz-experimental/QueryBuilder';

import type { MetricForm } from '../../../CreateMetricModal/formUtils';
import { MetricEventQueryBuilder } from '../../../CreateMetricModal/MetricEventQueryBuilder';
import {
  combineEventFilters,
  eventFilterToQuery,
  hasContextKindConflict,
  queryToEventFilter,
  splitFilterByCategory,
} from '../../../CreateMetricModal/MetricEventQueryBuilder/eventQueryConversion';
import { validateFilter } from '../MetricEventFilters/filterUtils';

interface EventQueryFieldProps {
  context: UseFormReturn<MetricForm>;
}

const FIELD_NAME = 'filters';

export const EventQueryField: React.FC<EventQueryFieldProps> = ({ context }) => {
  const {
    control,
    trigger,
    formState: { errors },
  } = context;

  // Freeze each bar's seed after that bar is edited so deferred defaultValues still seed the other.
  const filters = useWatch({ control, name: FIELD_NAME });
  const split = filters ? splitFilterByCategory(filters) : undefined;
  const contextStoredQuery = split?.contextFilter ? eventFilterToQuery(split.contextFilter) : undefined;
  const eventStoredQuery = split?.eventFilter ? eventFilterToQuery(split.eventFilter) : undefined;
  const contextEditedRef = useRef(false);
  const eventEditedRef = useRef(false);
  const [contextSeed, setContextSeed] = useState<string | undefined>(contextStoredQuery);
  const [eventSeed, setEventSeed] = useState<string | undefined>(eventStoredQuery);

  useEffect(() => {
    if (!contextEditedRef.current) {
      setContextSeed(contextStoredQuery);
    }
  }, [contextStoredQuery]);
  useEffect(() => {
    if (!eventEditedRef.current) {
      setEventSeed(eventStoredQuery);
    }
  }, [eventStoredQuery]);

  // Conflict lives only in live tokens (pin never persisted) — block save via validate ref + re-trigger.
  const [conflict, setConflict] = useState(false);
  const conflictRef = useRef(false);

  // Seeded multi-kind filters must invalidate before the user edits (onChange alone would miss that).
  useEffect(() => {
    if (contextEditedRef.current) {
      return;
    }
    const nextConflict = hasContextKindConflict(parseQuery(contextStoredQuery ?? ''));
    conflictRef.current = nextConflict;
    setConflict(nextConflict);
  }, [contextStoredQuery]);

  useEffect(() => {
    void trigger(FIELD_NAME);
  }, [conflict, trigger]);

  return (
    <Controller
      control={control}
      name={FIELD_NAME}
      rules={{ validate: (value) => !conflictRef.current && validateFilter(value) }}
      render={({ field }) => (
        <MetricEventQueryBuilder
          contextDefaultValue={contextSeed}
          eventDefaultValue={eventSeed}
          inputId={FIELD_NAME}
          errorMessage={errors.filters?.message}
          onChange={(bars) => {
            if (bars.editedBar === 'context') {
              contextEditedRef.current = true;
            } else {
              eventEditedRef.current = true;
            }
            const nextConflict = hasContextKindConflict(bars.contextTokens);
            conflictRef.current = nextConflict;
            setConflict(nextConflict);
            field.onChange(
              combineEventFilters(queryToEventFilter(bars.contextTokens), queryToEventFilter(bars.eventTokens)),
            );
          }}
        />
      )}
    />
  );
};
